import { useCallback } from 'react'
import type { MouseEvent } from 'react'
import { Link } from 'react-router-dom'
import Icon from './Icon'
import { trackContact } from '../utils/facebookPixel'
import { trackGAContact } from '../utils/googleAnalytics'
import { useIubenda } from '../hooks/useIubenda'

const socials = [
  {
    label: 'Instagram',
    href: 'https://www.instagram.com/jobtrackr.it/',
    iconType: 'instagram' as const,
  },
  {
    label: 'Facebook',
    href: 'https://www.facebook.com/jobtrackr.it',
    iconType: 'facebook' as const,
  },
  {
    label: 'LinkedIn',
    href: 'https://www.linkedin.com/company/job-trackr-it/',
    iconType: 'linkedin' as const,
  },
  {
    label: 'X',
    href: 'https://x.com/jobtrackr_it',
    iconType: 'x' as const,
  },
]

export const Footer = () => {
  // Initialize iubenda widgets after component mounts
  useIubenda()

  const handlePreferencesClick = useCallback((event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault()

    const api = window._iub?.cs?.api
    if (api?.openPreferences) {
      api.openPreferences()
      return
    }

    window.open('https://www.iubenda.com/privacy-policy/14898698/cookie-policy', '_blank', 'noopener,noreferrer')
  }, [])

  const handleUsprClick = useCallback((event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault()

    const ui = window._iub?.cs?.ui
    if (ui?.showPPUsprSection) {
      ui.showPPUsprSection(false)
      return
    }

    window.open('https://www.iubenda.com/privacy-policy/14898698/privacy-policy', '_blank', 'noopener,noreferrer')
  }, [])

  return (
    <footer className="border-t border-app-line bg-app-surface">
    <div className="mx-auto max-w-6xl px-4 py-8">
      <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
        {/* Logo and tagline */}
        <div>
          <Link to="/" className="inline-flex items-center transition hover:opacity-80">
            {/* Light mode - black logo */}
            <img 
              src="/logo-header-black.svg" 
              alt="JobTrackr.it - Job Application Tracker" 
              className="h-12 w-auto dark:hidden"
            />
            {/* Dark mode - white logo */}
            <img 
              src="/logo-header-white.svg" 
              alt="JobTrackr.it - Job Application Tracker" 
              className="h-12 w-auto hidden dark:block"
            />
          </Link>
        </div>

        {/* Contact */}
        <div>
          <h3 className="mb-3 text-sm font-semibold text-app-ink font-display">Contact</h3>
          <nav className="flex flex-col gap-2">
            <a
              href="/about"
              className="text-sm text-app-muted transition hover:text-app-ink"
            >
              About
            </a>
            <a
              href="/blog"
              className="text-sm text-app-muted transition hover:text-app-ink"
            >
              Blog
            </a>
            <a
              href="mailto:contact@jobtrackr.it"
              onClick={() => { trackContact('footer'); trackGAContact('footer') }}
              className="text-sm text-app-muted transition hover:text-app-ink"
            >
              contact@jobtrackr.it
            </a>
          </nav>
        </div>

        {/* Legal links */}
        <div>
          <h3 className="mb-3 text-sm font-semibold text-app-ink font-display">Legal</h3>
          <nav className="flex flex-col gap-2">
            <a
              className="text-sm text-app-muted transition hover:text-app-ink"
              href="/terms-and-conditions.html"
              rel="noreferrer"
            >
              Terms &amp; Conditions
            </a>
            <a
              className="text-sm text-app-muted transition hover:text-app-ink"
              href="/privacy"
            >
              Privacy Policy
            </a>
            <a
              className="text-sm text-app-muted transition hover:text-app-ink"
              href="/cookie-policy"
            >
              Cookie Policy
            </a>
            <a
              href="#"
              onClick={handlePreferencesClick}
              className="iubenda-cs-preferences-link text-sm text-app-muted transition hover:text-app-ink inline-flex items-center gap-2"
            >
              <img style={{ width: '2rem' }} src="data:image/svg+xml;charset=UTF-8,%3csvg version='1.1' id='Layer_1' xmlns='http://www.w3.org/2000/svg' xmlns:xlink='http://www.w3.org/1999/xlink' x='0px' y='0px' viewBox='0 0 30 14' style='enable-background:new 0 0 30 14;' xml:space='preserve'%3e%3cstyle type='text/css'%3e .st0%7bfill-rule:evenodd;clip-rule:evenodd;fill:%23FFFFFF;%7d .st1%7bfill-rule:evenodd;clip-rule:evenodd;fill:%230066FF;%7d .st2%7bfill:%23FFFFFF;%7d .st3%7bfill:%230066FF;%7d %3c/style%3e%3cg%3e%3cg id='final---dec.11-2020_1_'%3e%3cg id='_x30_208-our-toggle_2_' transform='translate(-1275.000000, -200.000000)'%3e%3cg id='Final-Copy-2_2_' transform='translate(1275.000000, 200.000000)'%3e%3cpath class='st0' d='M7.4,12.8h6.8l3.1-11.6H7.4C4.2,1.2,1.6,3.8,1.6,7S4.2,12.8,7.4,12.8z'/%3e%3c/g%3e%3c/g%3e%3c/g%3e%3cg id='final---dec.11-2020'%3e%3cg id='_x30_208-our-toggle' transform='translate(-1275.000000, -200.000000)'%3e%3cg id='Final-Copy-2' transform='translate(1275.000000, 200.000000)'%3e%3cpath class='st1' d='M22.6,0H7.4c-3.9,0-7,3.1-7,7s3.1,7,7,7h15.2c3.9,0,7-3.1,7-7S26.4,0,22.6,0z M1.6,7c0-3.2,2.6-5.8,5.8-5.8 h9.9l-3.1,11.6H7.4C4.2,12.8,1.6,10.2,1.6,7z'/%3e%3cpath id='x' class='st2' d='M24.6,4c0.2,0.2,0.2,0.6,0,0.8l0,0L22.5,7l2.2,2.2c0.2,0.2,0.2,0.6,0,0.8c-0.2,0.2-0.6,0.2-0.8,0 l0,0l-2.2-2.2L19.5,10c-0.2,0.2-0.6,0.2-0.8,0c-0.2-0.2-0.2-0.6,0-0.8l0,0L20.8,7l-2.2-2.2c-0.2-0.2-0.2-0.6,0-0.8 c0.2-0.2,0.6-0.2,0.8,0l0,0l2.2,2.2L23.8,4C24,3.8,24.4,3.8,24.6,4z'/%3e%3cpath id='y' class='st3' d='M12.7,4.1c0.2,0.2,0.3,0.6,0.1,0.8l0,0L8.6,9.8C8.5,9.9,8.4,10,8.3,10c-0.2,0.1-0.5,0.1-0.7-0.1l0,0 L5.4,7.7c-0.2-0.2-0.2-0.6,0-0.8c0.2-0.2,0.6-0.2,0.8,0l0,0L8,8.6l3.8-4.5C12,3.9,12.4,3.9,12.7,4.1z'/%3e%3c/g%3e%3c/g%3e%3c/g%3e%3c/g%3e%3c/svg%3e" alt="California Consumer Privacy Act (CCPA) Opt-Out Icon"/>Your Privacy Choices
            </a>
            <a
              href="#"
              onClick={handleUsprClick}
              className="iubenda-cs-uspr-link text-sm text-app-muted transition hover:text-app-ink"
            >
              Notice at Collection
            </a>
          </nav>
        </div>

        {/* Social links */}
        <div>
          <h3 className="mb-3 text-sm font-semibold text-app-ink font-display">Follow Us</h3>
          <div className="flex items-center gap-4">
            {socials.map((item) => (
              <a
                key={item.label}
                href={item.href}
                target="_blank"
                rel="noreferrer"
                aria-label={item.label}
                className="text-app-muted transition hover:text-app-ink"
              >
                <Icon type={item.iconType} />
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* Copyright */}
      <div className="mt-8 border-t border-app-line pt-6">
        <p className="text-center text-sm text-app-muted" translate="no">
          © {new Date().getFullYear()} JobTrackr.it. All rights reserved.
        </p>
      </div>
    </div>
  </footer>
  )
}

export default Footer
